import { useState } from "react";
import { BsChevronLeft } from "react-icons/bs";
import { FiUser } from "react-icons/fi";
import { HiOutlineMenuAlt2, HiOutlineUserGroup } from "react-icons/hi";
import { IoBusinessOutline, IoLogOutOutline } from "react-icons/io5";
import { RiBillLine } from "react-icons/ri";
import { useNavigate, useLocation } from "react-router-dom";
import { IconType } from "react-icons";
import { usePermissions } from "../../../contexts/PermissionContext";
import { checkPermissions } from "../../../utils/checkPermissions";
import { useLogout } from "../../../api/authApi";
import ConfirmationDialog from "../../../components/ConfirmationDialog";

interface NavButtonProps {
  icon: IconType;
  label: string;
  onClick?: () => void;
  isActive?: boolean;
}
//
const LeftSide = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname.split("/").pop();
  const { permissionLevel } = usePermissions();
  const logout = useLogout();

  const NavButton = ({
    icon: Icon,
    label,
    onClick,
    isActive = false,
  }: NavButtonProps) => {
    const baseStyle = "w-full rounded-lg flex items-center";
    const collapsedStyle = isCollapsed ? "justify-center px-0" : "px-4";
    const activeStyle = isActive
      ? "bg-[#78C7D1] text-white"
      : "text-gray-600 hover:bg-gray-50";

    return (
      <div
        className={`flex items-center justify-between rounded-lg ${activeStyle} relative group`}
      >
        <button
          className={`${baseStyle} ${collapsedStyle} py-2 ${
            isCollapsed ? "" : "ml-6"
          }`}
          onClick={onClick}
        >
          <Icon className={`text-xl ${!isCollapsed && "mr-3"}`} />
          {!isCollapsed && <span className="text-left text-sm">{label}</span>}
        </button>
        {isCollapsed && (
          <div className="absolute left-full ml-2 pl-1 hidden group-hover:block z-50">
            <div className="bg-gray-800 text-white text-sm py-1 px-3 rounded-md whitespace-nowrap">
              {label}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`${
        isCollapsed ? "w-16" : "w-66"
      } bg-white border-r border-gray-200 transition-all duration-300 h-full overflow-y-auto`}
    >
      <div className={`${isCollapsed ? "px-2" : "px-6"} py-4`}>
        <div
          className={`flex items-center mb-10 ${
            isCollapsed ? "justify-center" : "justify-between"
          }`}
        >
          {!isCollapsed && <h1 className="text-2xl font-semibold">Setting</h1>}
          <button
            className="flex items-center justify-center w-8 h-8 rounded hover:bg-gray-100 text-gray-600 hover:text-gray-800"
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            {isCollapsed ? (
              <HiOutlineMenuAlt2 size={20} />
            ) : (
              <BsChevronLeft size={20} />
            )}
          </button>
        </div>

        {/* Profile Menu Items */}
        <div className="space-y-1 mb-8">
          <NavButton
            icon={FiUser}
            label="My profile"
            onClick={() => navigate("myprofile")}
            isActive={currentPath === "myprofile"}
          />
          {permissionLevel &&
            checkPermissions(permissionLevel, "access", "myBusinessMenu") && (
              <NavButton
                icon={IoBusinessOutline}
                label="My business"
                onClick={() => navigate("mybusiness")}
                isActive={currentPath === "mybusiness"}
              />
            )}
          <NavButton
            icon={HiOutlineUserGroup}
            label="My team"
            onClick={() => navigate("myteam")}
            isActive={currentPath === "myteam"}
          />
        </div>

        {/* Manage Section */}
        <div className="mb-8">
          {!isCollapsed && (
            <h2 className="text-xs font-medium text-gray-500 mb-2 px-4">
              Manage
            </h2>
          )}
          <div className="space-y-1">
            <NavButton
              icon={IoBusinessOutline}
              label="Business & Communication"
              onClick={() => navigate("business")}
              isActive={currentPath === "business"}
            />
            {permissionLevel &&
              checkPermissions(permissionLevel, "access", "billingMenu") && (
                <NavButton
                  icon={RiBillLine}
                  label="Reports & Billing"
                  onClick={() => navigate("reports")}
                  isActive={currentPath === "reports"}
                />
              )}
          </div>
        </div>

        {/* Other Sections */}
        <div className="space-y-1">
          <h2 className="text-xs font-medium text-gray-500 mb-2 px-4">More</h2>
          <NavButton
            icon={IoLogOutOutline}
            label="Logout"
            onClick={() => setShowLogoutDialog(true)}
          />
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showLogoutDialog}
        onClose={() => setShowLogoutDialog(false)}
        onConfirm={() => {
          logout.mutateAsync();
          setShowLogoutDialog(false);
        }}
        title="Confirm Logout"
        message="Are you sure you want to logout? You will need to sign in again to access your account."
        confirmText="Logout"
        cancelText="Cancel"
        variant="warning"
      />
    </div>
  );
};

export default LeftSide;
